import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, getWorkersHealth, createJob } from '../../services/syncApi';
import { getSummary as getSchedulerSummary } from '../../services/schedulerApi';
import { listEpicorJobs } from '../../services/epicorApi';
import SyncAllMarketsCard from '../sync/SyncAllMarketsCard';
import { ENVIRONMENTS } from '../../constants/markets';
import {
  PageHeader,
  Card,
  StatGrid,
  StatCard,
  Badge,
  StatusBadge,
  Button,
} from '../ui';

const formatAge = (iso) => {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

const formatUntil = (iso) => {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'due now';
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `in ${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `in ${hours}h ${minutes % 60}m`;
  return `in ${Math.floor(hours / 24)}d`;
};

const summariseDiffs = (diffs) => {
  let inSync = 0, mismatches = 0, missing = 0;
  for (const r of diffs) {
    if (r.status === 'in_sync') inSync += r.cnt;
    else if (r.status.includes('missing')) missing += r.cnt;
    else mismatches += r.cnt;
  }
  return { inSync, mismatches, missing, total: inSync + mismatches + missing };
};

const JobsDashboard = () => {
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [schedSummary, setSchedSummary] = useState(null);
  const [epicorJobs, setEpicorJobs] = useState([]);
  const [error, setError] = useState(null);
  const [enqueueState, setEnqueueState] = useState({});

  const load = useCallback(async () => {
    try {
      const [d, h] = await Promise.all([getDashboard(), getWorkersHealth()]);
      setData(d);
      setHealth(h);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
    // Secondary feeds are non-fatal — the dashboard still works without them.
    getSchedulerSummary().then(setSchedSummary).catch(() => {});
    listEpicorJobs({ limit: 5 }).then((j) => setEpicorJobs(j || [])).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    return () => clearInterval(interval);
  }, [load]);

  const enqueueCompare = async (market_code, environment = 'prod') => {
    const key = `${environment}:${market_code}`;
    setEnqueueState((s) => ({ ...s, [key]: 'queueing' }));
    try {
      const r = await createJob({ store_code: market_code, environment, mode: 'compare', force_fresh: false });
      setEnqueueState((s) => ({ ...s, [key]: `Queued as job ${r.job_id}` }));
    } catch (e) {
      setEnqueueState((s) => ({ ...s, [key]: `Error: ${e.response?.data?.detail || e.message}` }));
    }
  };

  if (error) return <div className="error">Failed to load dashboard: {error}</div>;
  if (!data) return <div className="loading">Loading dashboard…</div>;

  const { inSync, mismatches, missing } = summariseDiffs(data.diffs_by_market_status || []);
  const runningJobs = health?.running_jobs?.length || 0;
  const staleJobs = health?.stale_running_jobs || 0;

  const schedStats = schedSummary && {
    active: schedSummary.schedules?.active ?? 0,
    total: schedSummary.schedules?.total ?? 0,
    nextRunAt: schedSummary.schedules?.next_run_at,
    nextName: schedSummary.upcoming?.[0]?.name,
    failures: schedSummary.last_24h?.failed ?? 0,
    runs24h: schedSummary.last_24h?.runs ?? 0,
  };

  return (
    <div className="jobs-dashboard">
      <PageHeader
        title="Jobs Dashboard"
        sub={`Last updated ${formatAge(data.generated_at)} · auto-refreshes every 15 s`}
        actions={
          <>
            <Button variant="secondary" to="/jobs/history">Job history</Button>
            <Button to="/jobs/run">Run a sync</Button>
          </>
        }
      />

      {/* ── KPI strip ── */}
      <StatGrid>
        <StatCard
          label="Products in sync"
          value={inSync.toLocaleString()}
          tone={inSync > 0 ? 'success' : undefined}
        />
        <StatCard
          label="Field mismatches"
          value={mismatches.toLocaleString()}
          sub={mismatches > 0 ? 'Review in Diff Explorer' : undefined}
          tone={mismatches > 0 ? 'warning' : undefined}
        />
        <StatCard
          label="Missing products"
          value={missing.toLocaleString()}
          tone={missing > 0 ? 'danger' : undefined}
        />
        <StatCard
          label="Jobs running now"
          value={runningJobs}
          sub={staleJobs > 0 ? `${staleJobs} stale (no heartbeat)` : undefined}
          tone={staleJobs > 0 ? 'warning' : 'primary'}
        />
      </StatGrid>

      {/* ── Action prompt ── */}
      {(mismatches > 0 || missing > 0) && (
        <div className="action-prompt">
          <div className="action-prompt-icon">💡</div>
          <div>
            <strong>What should you do next?</strong>
            {mismatches > 0 && (
              <p>There are <strong>{mismatches.toLocaleString()}</strong> field mismatches — open{' '}
                <Link to="/jobs/config/diffs">Diff Explorer</Link> to review and push updates.</p>
            )}
            {missing > 0 && (
              <p><strong>{missing.toLocaleString()}</strong> products are missing from BigCommerce — trigger a sync using the card below.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Sync trigger ── */}
      <SyncAllMarketsCard />

      {/* ── Scheduler summary ── */}
      <Card
        title="Scheduled syncs"
        desc="Recurring Epicor syncs managed by the scheduler."
        actions={<Button variant="secondary" size="sm" to="/jobs/schedules">Open scheduler</Button>}
      >
        {schedStats ? (
          <StatGrid>
            <StatCard
              label="Active schedules"
              value={schedStats.active}
              sub={`of ${schedStats.total} configured`}
            />
            <StatCard
              label="Next run"
              value={schedStats.nextRunAt ? formatUntil(schedStats.nextRunAt) : '—'}
              sub={schedStats.nextName || 'Nothing scheduled'}
            />
            <StatCard
              label="Runs · 24 h"
              value={schedStats.runs24h}
            />
            <StatCard
              label="Failures · 24 h"
              value={schedStats.failures}
              tone={schedStats.failures > 0 ? 'danger' : 'success'}
              sub={schedStats.failures > 0 ? 'Needs attention' : 'All clear'}
            />
          </StatGrid>
        ) : (
          <p className="muted">Scheduler summary unavailable.</p>
        )}
      </Card>

      {/* ── Diffs by market ── */}
      <Card
        title="Product differences by market"
        desc="How many products differ between Akeneo and BigCommerce, grouped by market (AU / NZ) and type of difference."
      >
        <table>
          <thead>
            <tr><th scope="col">Env</th><th scope="col">Market</th><th scope="col">Status</th><th scope="col">Count</th></tr>
          </thead>
          <tbody>
            {data.diffs_by_market_status.map((row) => (
              <tr key={`${row.environment || 'prod'}-${row.market_code}-${row.status}`}>
                <td><Badge variant={(row.environment || 'prod') === 'staging' ? 'warning' : 'neutral'}>{row.environment || 'prod'}</Badge></td>
                <td><Badge variant="primary">{row.market_code}</Badge></td>
                <td><StatusBadge status={row.status} /></td>
                <td>{row.cnt.toLocaleString()}</td>
              </tr>
            ))}
            {data.diffs_by_market_status.length === 0 && (
              <tr><td colSpan={4} className="muted">No comparison data yet — trigger a compare run below.</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* ── Top mismatched fields ── */}
      <Card
        title="Most common field differences"
        desc="Fields that most frequently differ between Akeneo and BigCommerce."
      >
        <table>
          <thead>
            <tr><th scope="col">Field</th><th scope="col">Type</th><th scope="col">Count</th></tr>
          </thead>
          <tbody>
            {data.top_mismatched_fields.map((row) => (
              <tr key={`${row.field_name}-${row.status}`}>
                <td><code>{row.field_name}</code></td>
                <td><StatusBadge status={row.status} /></td>
                <td>{row.cnt.toLocaleString()}</td>
              </tr>
            ))}
            {data.top_mismatched_fields.length === 0 && (
              <tr><td colSpan={3} className="muted">No mismatched fields — everything looks good!</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* ── Snapshots ── */}
      <Card
        title="Cached product snapshots"
        desc='Products cached from BigCommerce. "Fresh" means fetched within the last 24 hours. Trigger a fresh compare to update stale data.'
      >
        <table>
          <thead>
            <tr>
              <th scope="col">Env</th><th scope="col">Market</th><th scope="col">Total cached</th><th scope="col">Fresh (24 h)</th>
              <th scope="col">Newest</th><th scope="col">Oldest</th><th scope="col">Action</th>
            </tr>
          </thead>
          <tbody>
            {ENVIRONMENTS.flatMap((env) => ['AU', 'NZ'].map((m) => {
              const key = `${env}:${m}`;
              const s = (data.snapshots_by_store || []).find((x) => x.store_code === m && (x.environment || 'prod') === env);
              return (
                <tr key={key}>
                  <td><Badge variant={env === 'staging' ? 'warning' : 'neutral'}>{env}</Badge></td>
                  <td><Badge variant="primary">{m}</Badge></td>
                  <td>{s?.total?.toLocaleString() || 0}</td>
                  <td>{s?.fresh?.toLocaleString() || 0}</td>
                  <td>{formatAge(s?.newest_fetched_at)}</td>
                  <td>{formatAge(s?.oldest_fetched_at)}</td>
                  <td>
                    <Button
                      size="sm"
                      onClick={() => enqueueCompare(m, env)}
                      disabled={enqueueState[key] === 'queueing'}
                    >
                      {enqueueState[key] === 'queueing' ? 'Queueing…' : 'Run compare'}
                    </Button>
                    {enqueueState[key] && enqueueState[key] !== 'queueing' && (
                      <span className={enqueueState[key].startsWith('Error') ? 'inline-error' : 'inline-success'}>
                        {' '}{enqueueState[key]}
                      </span>
                    )}
                  </td>
                </tr>
              );
            }))}
          </tbody>
        </table>
      </Card>

      {/* ── Workers ── */}
      <Card
        title="Background workers"
        desc="Workers process sync jobs in the background. A stale worker has stopped sending heartbeats for over 2 minutes."
      >
        <div className="stat-row">
          <span>Running jobs: <strong>{runningJobs}</strong></span>
          <span>
            Stale: <strong className={staleJobs > 0 ? 'text-warning' : ''}>{staleJobs}</strong>
          </span>
        </div>
        {Object.keys(health?.rate_limits || {}).length > 0 && (
          <>
            <h3 className="section-subtitle">API rate limits</h3>
            <table>
              <thead>
                <tr>
                  <th scope="col">Store</th><th scope="col">Requests left</th><th scope="col">Window quota</th>
                  <th scope="col">Resets in</th><th scope="col">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(health.rate_limits).map(([store, r]) => (
                  <tr key={store}>
                    <td><Badge variant="primary">{store}</Badge></td>
                    <td>{r.requests_left ?? '—'}</td>
                    <td>{r.window_quota ?? '—'}</td>
                    <td>{r.reset_in_ms != null ? `${(r.reset_in_ms / 1000).toFixed(1)} s` : '—'}</td>
                    <td>{r.last_observed_at ? new Date(r.last_observed_at * 1000).toLocaleTimeString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {health?.concurrency_model && (
          <p className="muted" style={{ marginTop: 8, fontSize: 12 }}>{health.concurrency_model}</p>
        )}
      </Card>

      {/* ── Recent activity across queues ── */}
      <Card
        title="Recent activity"
        desc="Jobs across the BigCommerce and Epicor queues."
        actions={<Link to="/jobs/history" className="card-header-link">View all →</Link>}
      >
        <h3 className="section-subtitle">BigCommerce — last 24 hours</h3>
        <table>
          <thead>
            <tr><th scope="col">Env</th><th scope="col">Market</th><th scope="col">Status</th><th scope="col">Count</th></tr>
          </thead>
          <tbody>
            {(data.jobs_last_24h || []).map((row) => (
              <tr key={`${row.environment || 'prod'}-${row.store_code}-${row.status}`}>
                <td><Badge variant={(row.environment || 'prod') === 'staging' ? 'warning' : 'neutral'}>{row.environment || 'prod'}</Badge></td>
                <td><Badge variant="primary">{row.store_code}</Badge></td>
                <td><StatusBadge status={row.status} /></td>
                <td>{row.cnt}</td>
              </tr>
            ))}
            {(!data.jobs_last_24h || data.jobs_last_24h.length === 0) && (
              <tr><td colSpan={4} className="muted">No jobs in the last 24 hours.</td></tr>
            )}
          </tbody>
        </table>

        <h3 className="section-subtitle">Epicor — latest jobs</h3>
        <table>
          <thead>
            <tr>
              <th scope="col">ID</th><th scope="col">Status</th><th scope="col">Companies</th>
              <th scope="col">Created by</th><th scope="col">Finished</th>
            </tr>
          </thead>
          <tbody>
            {epicorJobs.map((j) => (
              <tr key={j.id}>
                <td>#{j.id}</td>
                <td><StatusBadge status={j.status} /></td>
                <td>{Array.isArray(j.payload?.companies) ? j.payload.companies.join(', ') : '—'}</td>
                <td className="muted">{j.created_by || '—'}</td>
                <td className="muted">{j.finished_at ? new Date(j.finished_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
            {epicorJobs.length === 0 && (
              <tr><td colSpan={5} className="muted">No Epicor jobs yet.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

export default JobsDashboard;
